import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Phone, Instagram, Send, MapPin, CheckCircle, Sparkles } from 'lucide-react';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    business: '',
    goal: '',
    message: '',
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // WhatsApp mesajı oluştur
    const whatsappNumber = '905516774334'; // +90 551 677 4334
    const message = `
🎨 *Yeni Teklif Talebi - ADK Creative*

👤 *Ad Soyad:* ${formData.name}
📱 *Telefon:* ${formData.phone}
🏢 *İşletme/Sektör:* ${formData.business || 'Belirtilmemiş'}
🎯 *Hedef:* ${formData.goal || 'Belirtilmemiş'}

📝 *Mesaj:*
${formData.message || 'Mesaj yok'}

---
Bu mesaj ADK Creative web sitesinden gönderilmiştir.
    `.trim();
    
    // WhatsApp URL'si oluştur
    const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    
    // WhatsApp'ı yeni sekmede aç
    window.open(whatsappURL, '_blank');
    
    // Başarı mesajını göster
    setIsSubmitted(true);
    
    // 5 saniye sonra formu sıfırla
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', phone: '', business: '', goal: '', message: '' });
    }, 5000);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const goalOptions = [
    'Sosyal medya',
    'Kurumsal kimlik',
    'Post/story',
    'Afiş/banner',
    'Reklam görseli'
  ];

  return (
    <section id="teklif-formu" className="py-24 bg-dark-grey relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 md:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div 
            initial={{ scale: 0, rotate: -180 }}
            whileInView={{ scale: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-gold/10 border border-gold/20 rounded-full"
          >
            <Sparkles className="w-4 h-4 text-gold" />
            <span className="text-gold text-sm font-mono uppercase tracking-wider">İletişim</span>
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-4xl md:text-6xl font-bold text-white mb-6"
          >
            Kısa bilgi bırakın,<br />
            <span className="text-gold">hemen dönüş yapalım</span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-gray-400 text-lg max-w-2xl mx-auto"
          >
            Projeniz hakkında konuşalım. Size en uygun çözümü birlikte bulalım.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <AnimatePresence mode="wait">
              {isSubmitted ? (
                <motion.div
                  key="success"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  exit={{ scale: 0, rotate: 180 }}
                  transition={{ type: "spring", stiffness: 200 }}
                  className="bg-gradient-to-br from-gold/20 to-transparent border border-gold/30 rounded-2xl p-12 text-center"
                >
                  <motion.div
                    animate={{ 
                      scale: [1, 1.2, 1],
                      rotate: [0, 360, 0]
                    }}
                    transition={{ 
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <CheckCircle className="w-20 h-20 text-gold mx-auto mb-6" />
                  </motion.div>
                  <h3 className="text-2xl font-bold text-white mb-4">Teşekkürler!</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Mesajınız alındı. En kısa sürede size dönüş yapacağız.
                  </p>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  {/* Name Field */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 }}
                  >
                    <label className="block text-gray-300 mb-2 font-medium">
                      Ad Soyad <span className="text-gold">*</span>
                    </label>
                    <motion.input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('name')}
                      onBlur={() => setFocusedField(null)}
                      required
                      animate={{
                        scale: focusedField === 'name' ? 1.02 : 1,
                      }}
                      className="w-full px-6 py-4 bg-black/40 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-gold/50 transition-all duration-300"
                      placeholder="Ahmet Yılmaz"
                    />
                  </motion.div>

                  {/* Phone Field */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                  >
                    <label className="block text-gray-300 mb-2 font-medium">
                      Telefon <span className="text-gold">*</span>
                    </label>
                    <motion.input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('phone')}
                      onBlur={() => setFocusedField(null)}
                      required
                      animate={{
                        scale: focusedField === 'phone' ? 1.02 : 1,
                      }}
                      className="w-full px-6 py-4 bg-black/40 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-gold/50 transition-all duration-300"
                      placeholder="+90 555 123 4567"
                    />
                  </motion.div>

                  {/* Business Field */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                  >
                    <label className="block text-gray-300 mb-2 font-medium">İşletme / Sektör</label>
                    <motion.input
                      type="text"
                      name="business"
                      value={formData.business}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('business')}
                      onBlur={() => setFocusedField(null)}
                      animate={{
                        scale: focusedField === 'business' ? 1.02 : 1,
                      }}
                      className="w-full px-6 py-4 bg-black/40 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-gold/50 transition-all duration-300"
                      placeholder="Örn: Kafe, E-ticaret"
                    />
                  </motion.div>

                  {/* Goal Dropdown */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 }}
                  >
                    <label className="block text-gray-300 mb-2 font-medium">Hedef</label>
                    <motion.select
                      name="goal"
                      value={formData.goal}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('goal')}
                      onBlur={() => setFocusedField(null)}
                      animate={{
                        scale: focusedField === 'goal' ? 1.02 : 1,
                      }}
                      className="w-full px-6 py-4 bg-black/40 border border-white/10 rounded-xl text-white focus:outline-none focus:border-gold/50 transition-all duration-300 cursor-pointer"
                    >
                      <option value="" className="bg-black">Seçiniz...</option>
                      {goalOptions.map((option, idx) => (
                        <option key={idx} value={option} className="bg-black">
                          {option}
                        </option>
                      ))}
                    </motion.select>
                  </motion.div>

                  {/* Message Field */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 }}
                  >
                    <label className="block text-gray-300 mb-2 font-medium">Kısa Not</label>
                    <motion.textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('message')}
                      onBlur={() => setFocusedField(null)}
                      rows={5}
                      animate={{
                        scale: focusedField === 'message' ? 1.02 : 1,
                      }}
                      className="w-full px-6 py-4 bg-black/40 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-gold/50 transition-all duration-300 resize-none"
                      placeholder="Projeniz hakkında kısa bilgi verin..."
                    />
                  </motion.div>

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className="group w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-gold text-black font-semibold rounded-full hover:bg-gold-light transform transition-all duration-300 shadow-lg hover:shadow-gold/50"
                  >
                    <motion.span
                      animate={{ x: [0, 5, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      Mesaj Gönder
                    </motion.span>
                    <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </motion.button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            {/* Info Card */}
            <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6">İletişim Bilgileri</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gold/10 rounded-xl">
                    <Phone className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Telefon</div>
                    <a
                      href="tel:+905516774334"
                      className="text-white hover:text-gold transition-colors duration-300"
                    >
                      +90 551 677 4334
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gold/10 rounded-xl">
                    <Mail className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm mb-1">E-posta</div>
                    <a
                      href="mailto:ahmetdemirkesen81@gmail.com"
                      className="text-white hover:text-gold transition-colors duration-300"
                    >
                      ahmetdemirkesen81@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gold/10 rounded-xl">
                    <Instagram className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Instagram</div>
                    <a
                      href="https://instagram.com/adkcreative"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white hover:text-gold transition-colors duration-300"
                    >
                      @adkcreative
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gold/10 rounded-xl">
                    <MapPin className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Konum</div>
                    <div className="text-white">Bartın, Türkiye</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Working Hours */}
            <div className="bg-gradient-to-br from-gold/10 to-transparent border border-gold/20 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">Çalışma Saatleri</h3>
              <div className="space-y-3 text-gray-300">
                <div className="flex justify-between">
                  <span>Pazartesi - Cuma</span>
                  <span className="text-gold font-semibold">09:00 - 18:00</span>
                </div>
                <div className="flex justify-between">
                  <span>Cumartesi</span>
                  <span className="text-gold font-semibold">10:00 - 16:00</span>
                </div>
                <div className="flex justify-between">
                  <span>Pazar</span>
                  <span className="text-gray-500">Kapalı</span>
                </div>
              </div>
            </div>

            {/* CTA Box */}
            <div className="bg-black/60 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center">
              <h3 className="text-xl font-bold text-white mb-3">Hızlı Yanıt Garantisi</h3>
              <p className="text-gray-400 mb-6">
                Tüm mesajlara 24 saat içinde dönüş yapıyoruz.
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/20 rounded-full text-gold text-sm">
                <div className="w-2 h-2 bg-gold rounded-full animate-pulse" />
                Aktif Durumdayız
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
